import axios from '../Axiosinstance'; // Ensure this is the correct path to your Axios instance
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  styled,
  TablePagination,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material';
import { useParams } from 'react-router-dom'; // Import useParams

// Styled components for improved aesthetics
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  padding: '12px',
  textAlign: 'center', // Center text for better alignment
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  height: '56px', // Set row height for uniformity
}));

const MarksheetTable = ({ students, loading }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Get unique module names from students, limit to the first six
  const uniqueModuleNames = Array.from(new Set(
    students.flatMap(student => student.moduleMarks.map(module => module.moduleName))
  )).slice(0, 6); // Only take the first six unique module names

  // Filter students based on search query
  const filteredStudents = students.filter(student =>
    student.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.nric.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={4} style={{ padding: '20px', borderRadius: '12px' }}>
      <TextField
        variant="outlined"
        label="Search by Name or NRIC"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{ marginBottom: '20px', width: '100%' }} // Full width for better usability
      />
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <StyledTableCell>S.No</StyledTableCell>
            <StyledTableCell>Student Name</StyledTableCell>
            <StyledTableCell>NRIC</StyledTableCell>
            {uniqueModuleNames.map((moduleName, index) => (
              <StyledTableCell key={index}>{moduleName} Total Mark</StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredStudents.length > 0 ? (
            filteredStudents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((student, index) => (
                <StyledTableRow key={index + 1}>
                  <TableCell>{index + 1 + page * rowsPerPage}</TableCell> {/* Correct serial number */}
                  <TableCell>{student.studentName}</TableCell>
                  <TableCell>{student.nric}</TableCell>
                  {uniqueModuleNames.map(moduleName => {
                    const module = student.moduleMarks.find(m => m.moduleName === moduleName) || {};
                    const totalMark = (module.mark1 || 0) + (module.mark2 || 0); // Sum mark1 and mark2
                    return (
                      <TableCell key={`module-${moduleName}-${index}`}>
                        {totalMark || '-'} {/* Display total mark or '-' if not available */}
                      </TableCell>
                    );
                  })}
                </StyledTableRow>
              ))
          ) : (
            <TableRow>
              <TableCell colSpan={uniqueModuleNames.length + 3} align="center">
                No results found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredStudents.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        style={{ padding: '10px 20px' }} // Add padding for better layout
      />
    </Paper>
  );
};

const App = () => {
  const [students, setStudents] = useState([]); // Ensure students is an array
  const [loading, setLoading] = useState(true); // Add loading state
  const { intakeId } = useParams(); // Get intakeId from URL parameters

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`intake/${intakeId}`); // Use Axios to get the data
        console.log('Fetched students:', response.data); // Log the fetched data
        if (Array.isArray(response.data)) {
          setStudents(response.data); // Only set if the response is an array
        } else {
          console.error('Expected an array but got:', response.data);
        }
      } catch (error) {
        console.error('Error fetching student data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [intakeId]); // Depend on intakeId

  return (
    <div>
      <Typography variant="h5" align="center" gutterBottom>
        Students Marks
      </Typography>
      <MarksheetTable students={students} loading={loading} />
    </div>
  );
};

export default App;
